/**
 * Gravação de voz no navegador. O formato depende do navegador (WebM no
 * Chrome, MP4 no Safari, OGG no Firefox); o servidor converte para OGG/Opus.
 */
import { Mic, Send, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Botao } from "@/components/ui/botao";

const FORMATOS = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4", "audio/webm"];

export function gravacaoSuportada(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function useGravador(onPronto: (audio: Blob, nome: string) => void) {
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const partes = useRef<Blob[]>([]);
  const descartar = useRef(false);
  const relogio = useRef<ReturnType<typeof setInterval> | null>(null);

  const parar = (enviar: boolean) => {
    descartar.current = !enviar;
    recorder.current?.stop();
  };

  const iniciar = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = FORMATOS.find((f) => MediaRecorder.isTypeSupported(f));
      const r = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      partes.current = [];
      descartar.current = false;
      r.ondataavailable = (e) => e.data.size && partes.current.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (relogio.current) clearInterval(relogio.current);
        setGravando(false);
        if (descartar.current || !partes.current.length) return;
        const tipo = r.mimeType || "audio/webm";
        const ext = tipo.includes("ogg") ? "ogg" : tipo.includes("mp4") ? "m4a" : "webm";
        onPronto(new Blob(partes.current, { type: tipo }), `voz.${ext}`);
      };
      r.start(250);
      recorder.current = r;
      setSegundos(0);
      setGravando(true);
      relogio.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    } catch {
      toast.error("Não foi possível usar o microfone. Verifique a permissão do navegador.");
    }
  };

  // Saiu da conversa no meio da gravação: descarta.
  useEffect(
    () => () => {
      descartar.current = true;
      recorder.current?.state === "recording" && recorder.current.stop();
    },
    [],
  );

  return { gravando, segundos, iniciar, parar };
}

export function BarraGravacao({
  segundos,
  onCancelar,
  onEnviar,
}: {
  segundos: number;
  onCancelar: () => void;
  onEnviar: () => void;
}) {
  const mm = String(Math.floor(segundos / 60)).padStart(1, "0");
  const ss = String(segundos % 60).padStart(2, "0");
  return (
    <div className="flex flex-1 items-center gap-3" role="status" aria-live="polite">
      <Botao
        variante="fantasma"
        tamanho="icone"
        className="size-11 text-danger-500"
        onClick={onCancelar}
        aria-label="Descartar gravação"
      >
        <Trash2 size={20} strokeWidth={1.75} />
      </Botao>
      <span className="flex flex-1 items-center gap-2 rounded-lg bg-danger-100 px-3 py-2.5 text-sm font-semibold text-danger-500">
        <Mic size={16} strokeWidth={2} className="animate-pulse" /> Gravando {mm}:{ss}
      </span>
      <Botao tamanho="icone" className="size-11" onClick={onEnviar} aria-label="Enviar áudio">
        <Send size={18} strokeWidth={1.75} />
      </Botao>
    </div>
  );
}
